// @tennis-matches/services/winterdoppel/costs

import { buildWinterDoppelPlayerDisplayNames } from "./playerDisplay.js";
import { buildWinterDoppelGroupCostBreakdown } from "./groupTrainingCosts.js";
import { eveningTotal, seasonAboTotal, splitCostShares } from "./planning.js";

function activeSessionIdsFrom(sessions = []) {
  return new Set(
    (sessions || [])
      .filter((s) => s?.id && s.status !== "cancelled")
      .map((s) => String(s.id)),
  );
}

/** Summe cost_share auf aktiven Terminen. */
export function sumWinterDoppelFrozenCostShares(
  sessionPlayers = [],
  sessions = [],
) {
  const activeSessionIds = activeSessionIdsFrom(sessions);
  let total = 0;
  for (const sp of sessionPlayers || []) {
    const sessionId = String(sp?.session_id || "");
    if (!sessionId || !activeSessionIds.has(sessionId)) continue;
    const share = Math.round((Number(sp.cost_share) || 0) * 100) / 100;
    if (share <= 0) continue;
    total = Math.round((total + share) * 100) / 100;
  }
  return total;
}

/** Summe credit_share auf aktiven Terminen. */
export function sumWinterDoppelFrozenCreditShares(
  sessionPlayers = [],
  sessions = [],
) {
  const activeSessionIds = activeSessionIdsFrom(sessions);
  let total = 0;
  for (const sp of sessionPlayers || []) {
    const sessionId = String(sp?.session_id || "");
    if (!sessionId || !activeSessionIds.has(sessionId)) continue;
    const credit = Math.round((Number(sp.credit_share) || 0) * 100) / 100;
    if (credit <= 0) continue;
    total = Math.round((total + credit) * 100) / 100;
  }
  return total;
}

/**
 * Saison-Abdeckung: Belastung − Guthaben (Neuzugang) ≈ Saison-Abo.
 */
export function assertWinterDoppelSeasonCostCoverage(
  sessionPlayers = [],
  series = {},
  billing = {},
  sessions = [],
) {
  const season = seasonAboTotal(
    billing?.hourly_rate,
    Math.max(1, Number(series?.court_count) || 1),
    {
      startTime: series?.start_time,
      endTime: series?.end_time,
    },
  );
  if (season <= 0) return;
  const costs = sumWinterDoppelFrozenCostShares(sessionPlayers, sessions);
  const credits = sumWinterDoppelFrozenCreditShares(sessionPlayers, sessions);
  const net = Math.round((costs - credits) * 100) / 100;
  if (Math.abs(net - season) > 0.01) {
    throw new Error("winterdoppel.costs.incompleteCoverage");
  }
}

function roundMoney(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

/** Echter Einsatz (Starter, nicht abgesagt). */
export function isWinterDoppelPlayingStarter(sp) {
  return Boolean(sp) && sp.role === "starter" && sp.rsvp !== "no";
}

/**
 * Saison-Anteil aus Publish-Neuzugang schon gebucht?
 * (Phantom-Zeilen oder Kosten + bereits gebuchtes Join-Guthaben bei anderen.)
 */
export function playerHasPublishedJoinSeasonCost(
  sessionPlayers = [],
  playerId,
) {
  const pid = String(playerId || "");
  if (!pid) return false;
  const mine = (sessionPlayers || []).filter(
    (sp) => String(sp?.player_id || "") === pid,
  );
  const myCost = mine.reduce((s, sp) => s + roundMoney(sp?.cost_share), 0);
  if (myCost <= 0) return false;
  if (
    mine.some(
      (sp) =>
        sp.role === "reserve" &&
        sp.rsvp === "no" &&
        roundMoney(sp.cost_share) > 0,
    )
  ) {
    return true;
  }
  const othersCredit = (sessionPlayers || [])
    .filter((sp) => String(sp?.player_id || "") !== pid)
    .reduce((s, sp) => s + roundMoney(sp?.credit_share), 0);
  return othersCredit > 0;
}

/** Nach Finalize angelegt → ungeplanter Serie-Neuzugang. */
export function isPostPublishSeriesJoiner(player, series) {
  const created = player?.created_at || player?.createdAt;
  const finalized = series?.finalized_at || series?.finalizedAt;
  if (!created || !finalized) return false;
  return new Date(created).getTime() > new Date(finalized).getTime();
}

/**
 * Post-Publish-Neuzugänger, die erstmals mitspielen und noch keinen Saison-Anteil haben.
 */
export function listPostPublishJoinersReadyForSeasonCost({
  players = [],
  series = {},
  sessionPlayers = [],
} = {}) {
  return (players || []).filter((p) => {
    if (!p?.id || !isPostPublishSeriesJoiner(p, series)) return false;
    if (playerHasPublishedJoinSeasonCost(sessionPlayers, p.id)) return false;
    return (sessionPlayers || []).some(
      (sp) =>
        String(sp?.player_id || "") === String(p.id) &&
        isWinterDoppelPlayingStarter(sp),
    );
  });
}

/**
 * Nach Veröffentlichung: Ungeplanter Neuzugang zahlt 1/N, sobald er mitspielt.
 * Belastung nur auf echte Einsätze. Bestehende behalten cost und erhalten Guthaben.
 */
export function redistributeCostsForPublishedPlayerJoin({
  sessionPlayers = [],
  sessions = [],
  newPlayerId,
  playerCount,
} = {}) {
  const newId = String(newPlayerId || "");
  const N = Math.max(0, Math.floor(Number(playerCount) || 0));
  const rows = (sessionPlayers || []).map((sp) => ({ ...sp }));
  const inserted = [];
  if (!newId || N < 2) {
    return { sessionPlayers: rows, transferred: 0, inserted };
  }

  const activeSessionIds = activeSessionIdsFrom(sessions);
  const playRows = rows.filter(
    (sp) =>
      sp &&
      String(sp.player_id || "") === newId &&
      activeSessionIds.has(String(sp.session_id || "")) &&
      isWinterDoppelPlayingStarter(sp),
  );
  if (!playRows.length) {
    return { sessionPlayers: rows, transferred: 0, inserted };
  }

  /** @type {Map<string, object[]>} */
  const donorRowsByPlayer = new Map();

  for (const sp of rows) {
    if (!sp || !activeSessionIds.has(String(sp.session_id || ""))) continue;
    const cost = roundMoney(sp.cost_share);
    if (cost <= 0) continue;
    const pid = String(sp.player_id || "");
    if (!pid || pid === newId) continue;
    const list = donorRowsByPlayer.get(pid) || [];
    list.push(sp);
    donorRowsByPlayer.set(pid, list);
  }

  let seasonTotal = 0;
  for (const list of donorRowsByPlayer.values()) {
    for (const sp of list)
      seasonTotal = roundMoney(seasonTotal + sp.cost_share);
  }
  if (seasonTotal <= 0) {
    return { sessionPlayers: rows, transferred: 0, inserted };
  }

  const nets = splitCostShares(seasonTotal, N);
  if (!nets.length || nets.every((n) => n <= 0)) {
    return { sessionPlayers: rows, transferred: 0, inserted };
  }
  const newTarget = nets[nets.length - 1];
  const donorNets = nets.slice(0, -1);

  const donors = [...donorRowsByPlayer.entries()]
    .map(([pid, list]) => ({
      pid,
      list,
      cost: roundMoney(
        list.reduce((s, sp) => s + roundMoney(sp.cost_share), 0),
      ),
    }))
    .sort((a, b) => b.cost - a.cost);

  let creditBooked = 0;
  for (let i = 0; i < donors.length; i += 1) {
    const { list, cost: playerCost } = donors[i];
    const desiredNet =
      i < donorNets.length ? donorNets[i] : roundMoney(seasonTotal / N);
    const creditNeed = roundMoney(playerCost - desiredNet);
    if (creditNeed <= 0) continue;
    let left = creditNeed;
    const sorted = list
      .slice()
      .sort((a, b) => roundMoney(b.cost_share) - roundMoney(a.cost_share));
    for (let j = 0; j < sorted.length; j += 1) {
      if (left <= 0) break;
      const sp = sorted[j];
      const room = roundMoney(sp.cost_share);
      let take =
        j === sorted.length - 1
          ? left
          : roundMoney((room / playerCost) * creditNeed);
      take = Math.min(take, left, room);
      if (take <= 0) continue;
      sp.credit_share = roundMoney((Number(sp.credit_share) || 0) + take);
      left = roundMoney(left - take);
      creditBooked = roundMoney(creditBooked + take);
    }
    if (left > 0 && sorted[0]) {
      sorted[0].credit_share = roundMoney(
        (Number(sorted[0].credit_share) || 0) + left,
      );
      creditBooked = roundMoney(creditBooked + left);
    }
  }

  const parts = splitCostShares(newTarget, playRows.length);
  let transferred = 0;
  for (let i = 0; i < playRows.length; i += 1) {
    const part = roundMoney(parts[i]);
    if (part <= 0) continue;
    playRows[i].cost_share = roundMoney(
      (Number(playRows[i].cost_share) || 0) + part,
    );
    transferred = roundMoney(transferred + part);
  }

  return { sessionPlayers: rows, transferred, inserted, creditBooked };
}

/**
 * Geplante Kosten je Spieler aus cost_share / credit_share der Session-Players.
 * frozen=true (nach Plan-Bestätigung): nur fixierte Anteile (cost_share > 0).
 *
 * @returns {{
 *   rows: Array<{
 *     playerId: string,
 *     displayName: string,
 *     evenings: number,
 *     lateJoinEvenings: number,
 *     total: number,
 *     credit: number,
 *     netTotal: number,
 *   }>,
 *   grandTotal: number,
 *   creditTotal: number,
 * }}
 */
export function buildWinterDoppelCostRows({
  players = [],
  sessions = [],
  sessionPlayers = [],
  frozen = false,
} = {}) {
  const activeSessionIds = activeSessionIdsFrom(sessions);

  /** @type {Map<string, { evenings: number, lateJoinEvenings: number, total: number, credit: number }>} */
  const stats = new Map();
  for (const p of players || []) {
    if (!p?.id) continue;
    stats.set(String(p.id), {
      evenings: 0,
      lateJoinEvenings: 0,
      total: 0,
      credit: 0,
    });
  }

  for (const sp of sessionPlayers || []) {
    const playerId = String(sp?.player_id || "");
    const sessionId = String(sp?.session_id || "");
    if (!playerId || !sessionId || !activeSessionIds.has(sessionId)) continue;

    const share = Math.round((Number(sp.cost_share) || 0) * 100) / 100;
    const credit = Math.round((Number(sp.credit_share) || 0) * 100) / 100;
    const isLateJoiner = Boolean(sp.substitute_for_player_id);

    if (frozen) {
      if (share <= 0 && credit <= 0) continue;
    } else if (sp.role !== "starter" || sp.rsvp === "no") {
      if (credit <= 0) continue;
    } else if (share <= 0 && credit <= 0) {
      continue;
    }

    if (!stats.has(playerId)) {
      stats.set(playerId, {
        evenings: 0,
        lateJoinEvenings: 0,
        total: 0,
        credit: 0,
      });
    }
    const row = stats.get(playerId);
    if (share > 0) {
      row.total = Math.round((row.total + share) * 100) / 100;
      // Termine = echte Einsätze (keine Phantom-Kostenzeilen reserve/rsvp=no)
      if (!frozen || (sp.role === "starter" && sp.rsvp !== "no")) {
        row.evenings += 1;
        if (isLateJoiner) row.lateJoinEvenings += 1;
      }
    }
    if (credit > 0) {
      // Guthaben aus ungeplantem Neuzugang (auch auf Zeilen mit Rest-Belastung)
      row.credit = Math.round((row.credit + credit) * 100) / 100;
    }
  }

  const nameById = buildWinterDoppelPlayerDisplayNames(players);

  const rows = [];
  let grandTotal = 0;
  let creditTotal = 0;
  for (const [
    playerId,
    { evenings, lateJoinEvenings, total, credit },
  ] of stats) {
    grandTotal = Math.round((grandTotal + total) * 100) / 100;
    creditTotal = Math.round((creditTotal + credit) * 100) / 100;
    rows.push({
      playerId,
      displayName: nameById.get(playerId) || playerId,
      evenings,
      lateJoinEvenings,
      total,
      credit,
      netTotal: Math.round((total - credit) * 100) / 100,
    });
  }

  rows.sort((a, b) => {
    if (a.netTotal !== b.netTotal) return b.netTotal - a.netTotal;
    return a.displayName.localeCompare(b.displayName, "de");
  });

  return { rows, grandTotal, creditTotal };
}

function clubPersonDisplayName(person) {
  if (!person) return "";
  const first = String(person.first_name || "").trim();
  const last = String(person.last_name || "").trim();
  return `${first} ${last}`.trim();
}

/**
 * Rechnungsempfänger für einen WinterDoppel-Spieler aus Stammdaten ableiten.
 * Ohne clubPeopleById: player.recipient_name (z. B. Public-Schedule-RPC) nutzen.
 */
export function resolveWinterDoppelBillingRecipient(
  player,
  clubPeopleById = {},
) {
  const lookup = (id) => {
    if (!id) return null;
    const key = String(id);
    if (clubPeopleById instanceof Map) return clubPeopleById.get(key) || null;
    return clubPeopleById[key] || null;
  };
  const person = lookup(player?.club_person_id || player?.clubPersonId);
  const playerName =
    String(player?.display_name || player?.displayName || "").trim() ||
    clubPersonDisplayName(person) ||
    String(player?.id || "");
  const preloadedRecipient = String(
    player?.recipient_name || player?.recipientName || "",
  ).trim();

  if (!person) {
    return {
      recipientKey: preloadedRecipient
        ? `recipient:${preloadedRecipient}`
        : `player:${player?.id || playerName}`,
      recipientName: preloadedRecipient || playerName,
    };
  }

  const recipientType = String(person.invoice_recipient_type || "self").trim();
  const billingContactId = String(person.billing_contact_id || "").trim();
  if (recipientType === "contact" && billingContactId) {
    const contact = lookup(billingContactId);
    const contactName =
      clubPersonDisplayName(contact) ||
      String(contact?.display_name || "").trim() ||
      preloadedRecipient ||
      billingContactId;
    return {
      recipientKey: `contact:${billingContactId}`,
      recipientName: contactName,
    };
  }

  const selfId = String(person.id || "").trim();
  return {
    recipientKey: `person:${selfId || player?.id}`,
    recipientName: clubPersonDisplayName(person) || playerName,
  };
}

/**
 * Kostenprognose Training: Zeilen je Spieler + Summe je Rechnungsempfänger.
 * Optional: Trainerkosten je Gruppe (hallShare + trainerShare).
 */
export function buildWinterDoppelTrainingCostForecast({
  players = [],
  sessions = [],
  sessionPlayers = [],
  series = {},
  billing = {},
  clubPeopleById = {},
  frozen = false,
  groups = [],
  trainersById = {},
  datesByGroupId = {},
} = {}) {
  const sessionCount = (sessions || []).filter(
    (s) => s?.status !== "cancelled",
  ).length;
  const courtCount = Math.max(1, Number(series?.court_count) || 1);
  const fee = Number(billing?.court_fee ?? series?.court_fee) || 0;
  const hourly = Number(billing?.hourly_rate ?? series?.hourly_rate) || 0;
  const perEvening = eveningTotal(fee, courtCount, {
    hourlyRate: hourly,
    startTime: series?.start_time,
    endTime: series?.end_time,
    sessionCount,
  });
  const season = seasonAboTotal(hourly, courtCount, {
    startTime: series?.start_time,
    endTime: series?.end_time,
  });
  const periodTotal =
    season > 0 ? season : Math.round(perEvening * sessionCount * 100) / 100;

  const groupCosts =
    Array.isArray(groups) && groups.length > 0
      ? buildWinterDoppelGroupCostBreakdown({
          groups,
          trainersById,
          players,
          series,
          billing,
          datesByGroupId,
        })
      : null;

  const cost = buildWinterDoppelCostRows({
    players,
    sessions,
    sessionPlayers,
    frozen,
  });

  const playerCount = Math.max(1, (players || []).length);
  const estimatedPerPlayer =
    cost.rows.length > 0 && cost.grandTotal > 0
      ? null
      : Math.round((periodTotal / playerCount) * 100) / 100;

  const playerRows = (players || []).map((p) => {
    const id = String(p.id);
    const existing = cost.rows.find((r) => r.playerId === id);
    const recipient = resolveWinterDoppelBillingRecipient(p, clubPeopleById);
    const groupPlayer = groupCosts?.playerRows?.find((r) => r.playerId === id);
    const hallShare =
      groupPlayer?.hallShare ??
      (estimatedPerPlayer != null ? estimatedPerPlayer : null);
    const trainerShare = groupPlayer?.trainerShare ?? 0;
    let total;
    if (existing != null) {
      total = frozen ? existing.netTotal : existing.total;
      if (groupCosts && trainerShare > 0) {
        total = Math.round((total + trainerShare) * 100) / 100;
      }
    } else if (groupPlayer) {
      total = groupPlayer.total;
    } else {
      total = estimatedPerPlayer || 0;
    }
    return {
      playerId: id,
      displayName: existing?.displayName || p.display_name || id,
      evenings: existing?.evenings ?? sessionCount,
      total,
      hallShare: hallShare ?? 0,
      trainerShare,
      recipientKey: recipient.recipientKey,
      recipientName: recipient.recipientName,
      estimated: existing == null,
    };
  });

  /** @type {Map<string, { recipientKey: string, recipientName: string, playerNames: string[], hallShare: number, trainerShare: number, total: number }>} */
  const byRecipient = new Map();
  for (const row of playerRows) {
    const cur = byRecipient.get(row.recipientKey) || {
      recipientKey: row.recipientKey,
      recipientName: row.recipientName,
      playerNames: [],
      hallShare: 0,
      trainerShare: 0,
      total: 0,
    };
    cur.playerNames.push(row.displayName);
    cur.hallShare =
      Math.round((cur.hallShare + (Number(row.hallShare) || 0)) * 100) / 100;
    cur.trainerShare =
      Math.round((cur.trainerShare + (Number(row.trainerShare) || 0)) * 100) /
      100;
    cur.total = Math.round((cur.total + row.total) * 100) / 100;
    byRecipient.set(row.recipientKey, cur);
  }

  const recipientRows = [...byRecipient.values()].sort((a, b) =>
    a.recipientName.localeCompare(b.recipientName, "de"),
  );

  return {
    periodTotal,
    perEvening,
    sessionCount,
    playerRows,
    recipientRows,
    estimated: Boolean(estimatedPerPlayer),
    trainerTotal: groupCosts?.trainerTotal ?? 0,
    hallTotal: groupCosts?.hallTotal ?? periodTotal,
    grandTotalWithTrainer:
      groupCosts?.grandTotal ??
      Math.round((periodTotal + (groupCosts?.trainerTotal || 0)) * 100) / 100,
    groupRows: groupCosts?.groupRows || [],
  };
}
